import React from 'react';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import axiosClient from '@/utils/client';
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  File as FileIcon,
  FileImage,
  FilePdf,
  Folder as FolderIconPhosphor,
  FolderPlus as FolderPlusIcon,
  Lock as LockIcon,
  MagnifyingGlass as SearchIcon,
  DotsThree as DotsThreeIcon,
  UploadSimple as UploadIcon,
} from '@phosphor-icons/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';

import { useNavigate } from 'react-router';

import type { CommunityFile } from '@/types/community';
import type { File } from '@/types/file';
import type { Folder } from '@/types/folder';
import { paths } from '@/paths';
import { useUser } from '@/hooks/v2/use-user';
import { useCommunityRole } from '@/hooks/v2/use-community-role';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import { Seo } from '@/components/seo';

import ViewDocumentDialog from '../dashboard/dialogs/ViewDocument';
import { CreateFolderDialog } from './components/create-folder-modal';
import { EditFolderDialog } from './components/edit-folder-modal';
import { EditFileDialog } from './components/edit-file-modal';
import { UploadFileDialog } from './components/upload-file-modal';

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'text.disabled',
        px: 3,
        py: 1.5,
      }}
    >
      {children}
    </Typography>
  );
}

function getFileIcon(mimeType?: string): React.ReactNode {
  if (!mimeType) return <FileIcon size={20} />;
  if (mimeType.includes('pdf')) return <FilePdf size={20} />;
  if (mimeType.includes('image') || mimeType.includes('jpeg') || mimeType.includes('png'))
    return <FileImage size={20} />;
  return <FileIcon size={20} />;
}

interface FolderRowProps {
  folder: Folder;
  onNavigate: (uuid: string) => void;
  onEdit: (item: any) => void;
  showActions: boolean;
}

function FolderRow({ folder, onNavigate, onEdit, showActions }: FolderRowProps) {
  const isPrivate = folder.visibility === 'private';

  return (
    <Stack
      direction="row"
      onClick={() => folder.uuid && onNavigate(folder.uuid)}
      sx={{
        alignItems: 'center',
        px: 3,
        py: 1.5,
        cursor: 'pointer',
        transition: 'background-color 150ms',
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      <Box sx={{ color: 'warning.main', display: 'flex', alignItems: 'center', mr: 2, flexShrink: 0 }}>
        <FolderIconPhosphor size={22} weight="fill" />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography noWrap sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
            {folder.name}
          </Typography>
          {isPrivate && (
            <Tooltip title="Private — only visible to managers/admins">
              <Box sx={{ display: 'flex', color: 'text.disabled' }}>
                <LockIcon size={13} />
              </Box>
            </Tooltip>
          )}
        </Stack>
        {folder.item_count > 0 && (
          <Typography color="text.secondary" variant="caption">
            {folder.item_count} item{folder.item_count !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>

      {showActions && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onEdit({ ...folder, type: 'folder' });
          }}
          size="small"
        >
          <DotsThreeIcon size={18} />
        </IconButton>
      )}
    </Stack>
  );
}

interface FileRowProps {
  file: any;
  onView: (file: any) => void;
  onEdit: (item: any) => void;
  showActions: boolean;
}

function FileRow({ file, onView, onEdit, showActions }: FileRowProps) {
  const isPrivate = file.visibility === 'private';

  return (
    <Stack
      direction="row"
      onClick={() => onView(file)}
      sx={{
        alignItems: 'center',
        px: 3,
        py: 1.5,
        cursor: 'pointer',
        transition: 'background-color 150ms',
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', mr: 2, flexShrink: 0 }}>
        {getFileIcon(file.mime_type)}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography noWrap sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
            {file.name_original || file.name}
          </Typography>
          {isPrivate && (
            <Tooltip title="Private">
              <Box sx={{ display: 'flex', color: 'text.disabled' }}>
                <LockIcon size={13} />
              </Box>
            </Tooltip>
          )}
        </Stack>
      </Box>

      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        {file.mime_type && (
          <Chip label={file.mime_type.split('/').pop()} size="small" sx={{ fontSize: '0.7rem', height: 20 }} variant="outlined" />
        )}
        {showActions && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onEdit({ ...file, type: 'file' });
            }}
            size="small"
          >
            <DotsThreeIcon size={18} />
          </IconButton>
        )}
      </Stack>
    </Stack>
  );
}

export function DocumentsPage(): React.JSX.Element {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<{ folders: Folder[]; files: File[] }>({
    folders: [],
    files: [],
  });
  const [isSearching, setIsSearching] = React.useState(false);
  const [selectedDocument, setSelectedDocument] = React.useState<CommunityFile | null>(null);
  const [editTarget, setEditTarget] = React.useState<any>(null);

  const [folderDialogOpen, setFolderDialogOpen] = React.useState(false);
  const [fileDialogOpen, setFileDialogOpen] = React.useState(false);
  const [viewDocumentOpen, setViewDocumentOpen] = React.useState(false);
  const [editFolderOpen, setEditFolderOpen] = React.useState(false);
  const [editFileOpen, setEditFileOpen] = React.useState(false);

  const { user } = useUser();
  // Documents deliberately uses hasBoardLevelAccess, not hasManagerAccess:
  // routes/api/v1/folders.php admits board_member for folder create/update/
  // delete and file upload, and the header's New Folder/Upload buttons are
  // RoleGuard-gated (which elevates them). Using hasManagerAccess here let a
  // board member create a folder but gave them no row edit/delete actions.
  const { hasBoardLevelAccess } = useCommunityRole();
  const navigate = useNavigate();

  const { data, refetch, isLoading } = useQuery({
    queryKey: ['folders'],
    queryFn: async () => {
      const response = await axiosClient.get('/api/v1/folders');
      return response.data;
    },
  });

  const folders: Folder[] = data?.folders || [];
  const files: File[] = data?.files || [];

  const searchMutation = useMutation({
    mutationFn: async (query: string) => {
      const response = await axiosClient.get(`/api/v1/folders/search?query=${query}`);
      return response.data;
    },
    onSuccess: (result) => {
      setSearchResults({ folders: result.folders || [], files: result.files || [] });
      setIsSearching(true);
    },
  });

  const debouncedSearch = React.useCallback(
    debounce((query: string) => {
      if (!query) {
        setIsSearching(false);
        setSearchResults({ folders: [], files: [] });
        return;
      }
      searchMutation.mutate(query);
    }, 300),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (!value) {
      setIsSearching(false);
      setSearchResults({ folders: [], files: [] });
      return;
    }
    debouncedSearch(value);
  };

  const handleNavigateFolder = (uuid: string) => {
    navigate(paths.documents.folder(uuid));
  };

  const handleViewFile = (file: any) => {
    setSelectedDocument(file as unknown as CommunityFile);
    setViewDocumentOpen(true);
  };

  const handleEditItem = (item: any) => {
    setEditTarget(item);
    if (item.type === 'folder') {
      setEditFolderOpen(true);
    } else {
      setEditFileOpen(true);
    }
  };

  const handleRefetch = async () => {
    await refetch();
  };

  const showActions = true; // managed by RoleGuard in the row

  const displayFolders = isSearching ? searchResults.folders : folders;
  const displayFiles = isSearching ? searchResults.files : files;
  const hasContent = displayFolders.length > 0 || displayFiles.length > 0;

  if (user?.selected_community_id === null) {
    return (
      <>
        <Seo title="Documents" />
        <Box component="main" sx={{ flexGrow: 1, backgroundColor: 'background.paper', minHeight: '100vh' }}>
          <Container maxWidth="xl" sx={{ py: 10, textAlign: 'center' }}>
            <FolderIconPhosphor color="var(--mui-palette-text-disabled)" size={64} style={{ marginBottom: 16 }} />
            <Typography sx={{ fontWeight: 600, mb: 1 }} variant="h5">
              Select a Community
            </Typography>
            <Typography color="text.secondary" variant="body2">
              To view documents, you must be viewing as a single community.
            </Typography>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <Seo title="Documents" />
      <Box component="main" sx={{ flexGrow: 1, backgroundColor: 'background.paper', minHeight: '100vh' }}>
        {/* Header strip */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: { xs: 2, md: 4 }, py: 3 }}>
          <Container disableGutters maxWidth="xl">
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '1.25rem' }}>Documents</Typography>
                <Typography color="text.secondary" sx={{ mt: 0.25 }} variant="body2">
                  Browse, upload, and manage community files and folders
                </Typography>
              </Box>
              <RoleGuard
                requiredAccountRoles={[ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER, ACCOUNT_ROLES.ACCOUNT_OWNER]}
                requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
              >
                <Stack direction="row" spacing={1}>
                  <Button
                    onClick={() => setFolderDialogOpen(true)}
                    size="small"
                    startIcon={<FolderPlusIcon size={16} />}
                    variant="outlined"
                  >
                    New Folder
                  </Button>
                  <Button
                    onClick={() => setFileDialogOpen(true)}
                    size="small"
                    startIcon={<UploadIcon size={16} />}
                    variant="contained"
                  >
                    Upload
                  </Button>
                </Stack>
              </RoleGuard>
            </Stack>
          </Container>
        </Box>

        {/* Body */}
        <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, md: 4 } }}>
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            {/* Search bar */}
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <TextField
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon size={18} />
                    </InputAdornment>
                  ),
                }}
                onChange={handleSearchChange}
                placeholder="Search files and folders..."
                size="small"
                value={searchQuery}
                variant="outlined"
              />
            </Box>

            {/* Content */}
            {isLoading ? (
              <Stack spacing={0}>
                {[1, 2, 3, 4].map((i) => (
                  <Box key={i} sx={{ px: 3, py: 1.5 }}>
                    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                      <Skeleton height={22} variant="circular" width={22} />
                      <Skeleton height={20} variant="text" width="40%" />
                    </Stack>
                  </Box>
                ))}
              </Stack>
            ) : !hasContent ? (
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <FolderIconPhosphor color="var(--mui-palette-text-disabled)" size={48} style={{ marginBottom: 12 }} />
                <Typography color="text.secondary" sx={{ fontWeight: 500 }} variant="body1">
                  {isSearching ? 'No results found' : 'No documents yet'}
                </Typography>
                <Typography color="text.disabled" sx={{ mt: 0.5 }} variant="body2">
                  {isSearching ? 'Try a different search term' : 'Create a folder or upload a file to get started'}
                </Typography>
              </Box>
            ) : (
              <>
                {displayFolders.length > 0 && (
                  <>
                    <SectionLabel>Folders</SectionLabel>
                    <Divider />
                    {displayFolders.map((folder: Folder, idx: number) => (
                      <React.Fragment key={folder.uuid || folder.id}>
                        <FolderRow
                          folder={folder}
                          onEdit={handleEditItem}
                          onNavigate={handleNavigateFolder}
                          showActions={hasBoardLevelAccess}
                        />
                        {idx < displayFolders.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </>
                )}

                {displayFiles.length > 0 && (
                  <>
                    {displayFolders.length > 0 && <Divider sx={{ my: 0 }} />}
                    <SectionLabel>Files</SectionLabel>
                    <Divider />
                    {displayFiles.map((file: any, idx: number) => (
                      <React.Fragment key={file.id}>
                        <FileRow file={file} onEdit={handleEditItem} onView={handleViewFile} showActions={hasBoardLevelAccess} />
                        {idx < displayFiles.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </>
                )}
              </>
            )}
          </Paper>
        </Container>
      </Box>

      {/* Dialogs */}
      <RoleGuard
        requiredAccountRoles={[ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER, ACCOUNT_ROLES.ACCOUNT_OWNER]}
        requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
      >
        <CreateFolderDialog
          onClose={() => setFolderDialogOpen(false)}
          onCreate={async () => {
            await handleRefetch();
            setFolderDialogOpen(false);
          }}
          open={folderDialogOpen}
        />
        <UploadFileDialog
          onClose={() => setFileDialogOpen(false)}
          onCreate={async () => {
            await handleRefetch();
            setFileDialogOpen(false);
          }}
          open={fileDialogOpen}
        />
        <EditFolderDialog
          folder={editTarget as unknown as Folder}
          onClose={() => {
            setEditFolderOpen(false);
            setEditTarget(null);
          }}
          onEdit={async () => {
            await handleRefetch();
            setEditFolderOpen(false);
            setEditTarget(null);
          }}
          onSuccess={async () => {
            await handleRefetch();
            setEditFolderOpen(false);
            setEditTarget(null);
          }}
          open={editFolderOpen}
        />
        <EditFileDialog
          file={editTarget as unknown as File}
          onClose={() => {
            setEditFileOpen(false);
            setEditTarget(null);
          }}
          onEdit={async () => {
            await handleRefetch();
            setEditFileOpen(false);
            setEditTarget(null);
          }}
          onSuccess={async () => {
            await handleRefetch();
            setEditFileOpen(false);
            setEditTarget(null);
          }}
          open={editFileOpen}
        />
      </RoleGuard>
      <ViewDocumentDialog
        file={selectedDocument}
        onClose={() => {
          setSelectedDocument(null);
          setViewDocumentOpen(false);
        }}
        open={viewDocumentOpen}
      />
    </>
  );
}
